import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isDefined, isEmpty, isNotDefined, isOfType } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { useSaveStoreSelector } from 'pages/audience/shared/save/hooks/context';
import { savePromptQueryKeys } from 'pages/audience/shared/save-prompt/utilities';
import { sharedABQueryKeys } from 'pages/audience/shared/utilities';
import { userService } from 'services';
import { selectUserId } from 'store/selectors';
import { AudienceStatus, IAudience, IAudienceShop, MultiRfmSearchApi } from 'types/api/audience';
import { IResult } from 'types/api/common';
import { SaveForm } from 'types/pages/audience-builder/shared/save/form';

export const useSaveShop = () => {
  const userId = useAppSelector(selectUserId);

  const queryClient = useQueryClient();
  const data = useSaveStoreSelector(({ data }) => data);

  const queryKey = savePromptQueryKeys.audiences(userId);

  return useMutation({
    mutationFn: async (formData: SaveForm) => {
      const { type } = formData;

      if (isNotDefined(userId)) {
        return Promise.reject();
      }

      if (type !== 'basic' || !isOfType<IAudienceShop>(data, 'categoryId')) {
        return Promise.reject();
      }

      const { client, description, giftDateEnd, giftDateStart, ...rest } = formData;
      const shop: IAudienceShop = {
        ...data,
        ...rest,
        clientId: client?.id ?? null,
        clientName: client?.name ?? null,
        description: isEmpty(description) ? null : description,
        giftDateEnd: giftDateEnd?.toISOString(),
        giftDateStart: giftDateStart?.toISOString(),
        quantity: 0,
        statusId: AudienceStatus.Active,
        userId,
      };

      const { parameters } = shop;

      if (isOfType<MultiRfmSearchApi>(parameters, 'rfmSets')) {
        return await userService.saveAudienceShopMultiRfm(shop);
      }

      return await userService.saveAudienceShop(shop);
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueriesData({ queryKey }, context?.snapshot);
    },
    onMutate: async (formData: SaveForm) => {
      const { type } = formData;

      await queryClient.cancelQueries({ queryKey });

      const snapshot = queryClient.getQueriesData<IResult<IAudience[]>>({ queryKey });

      if (type !== 'basic') {
        return { snapshot };
      }

      queryClient.setQueriesData<IResult<IAudience[]>>({ queryKey }, (oldData) => {
        if (isDefined(oldData)) {
          let { dataContainer } = oldData;

          if (isNotDefined(dataContainer)) {
            return oldData;
          }

          dataContainer = [
            ...dataContainer,
            {
              category: {
                id: formData.categoryId,
                name: '',
              },
              clientId: formData.client?.id ?? undefined,
              clientName: formData.client?.name ?? undefined,
              id: formData.id ?? '',
              name: formData.name,
              orderNumber: formData.orderNumber ?? undefined,
              quantity: formData.quantity,
              shops: [],
              userId,
            },
          ];

          oldData = {
            ...oldData,
            dataContainer: [...dataContainer],
          };

          return oldData;
        }

        return oldData;
      });

      return {
        snapshot,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onSuccess: (result, formData) => {
      if (result?.success) {
        if (isOfType<IAudienceShop>(formData, 'categoryId') && isDefined(formData.shopId)) {
          queryClient.invalidateQueries({ queryKey: sharedABQueryKeys.shop.default(formData.shopId) });
        }
      }
    },
  });
};
